import { lazy, Suspense, useEffect, useState } from 'react';
import type { ReactNode } from 'react';
import { BrowserRouter } from 'react-router';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { I18nextProvider } from 'react-i18next';
import { ServicesContext } from '@/model';
import type { HyloServices } from '@/model';
import { i18next, registerNamespace } from '@/i18n';
import { FEATURES } from '@/features/registry';
import type { FeatureModule } from './contract';
import { Environment } from './environment';
import { ErrorBoundary } from './ErrorBoundary';
import { PresentationProvider } from './presentations';
import { PWAUpdate } from './pwa';
import { useLoginSession } from './login-session';
import { FeatureRuntimes } from './FeatureRuntimes';
import { markShellInteractive } from './offline-install';
import en from './locales/en.json';
import zh from './locales/zh-Hans.json';
import './shell.css';

registerNamespace('shell', { en, 'zh-Hans': zh });
const Shell = lazy(() => import('./Shell'));
const Onboarding = lazy(() => import('./Onboarding'));

export function App({ services, features = FEATURES }: { services: HyloServices; features?: readonly FeatureModule[] }) {
  return <I18nextProvider i18n={i18next}><ServicesContext.Provider value={services}>
    <FeatureRuntimes phase="services" />
    <Environment />
    <BrowserRouter><ErrorBoundary><SessionGate features={features} /></ErrorBoundary></BrowserRouter>
    <PWAUpdate />
  </ServicesContext.Provider></I18nextProvider>;
}

function SessionGate({ features }: { features: readonly FeatureModule[] }) {
  const { session, key: lifetime } = useLoginSession();
  // Account changes discard resource caches, UI state and pending presentations together.
  const key = session.phase === 'signedIn' ? `${session.accountId}:${lifetime}` : session.phase;
  return <AccountBoundary key={key}>
    <ErrorBoundary resetKey={key}><PresentationProvider><Suspense fallback={<LaunchSplash />}>
      {session.phase === 'restoring' ? <LaunchSplash /> : session.phase === 'signedOut' ? <Onboarding reason={session.reason} /> : <Shell features={features} accountId={session.accountId} />}
      {session.phase !== 'restoring' && <ForegroundReady />}
    </Suspense></PresentationProvider></ErrorBoundary>
  </AccountBoundary>;
}

function ForegroundReady() {
  useEffect(() => {
    let second = 0;
    const first = requestAnimationFrame(() => { second = requestAnimationFrame(markShellInteractive); });
    return () => { cancelAnimationFrame(first); cancelAnimationFrame(second); };
  }, []);
  return null;
}

function AccountBoundary({ children }: { children: ReactNode }) {
  const [client] = useState(() => new QueryClient({ defaultOptions: { queries: { staleTime: 30_000, retry: 1, refetchOnWindowFocus: true }, mutations: { retry: false } } }));
  useEffect(() => () => { void client.cancelQueries(); client.clear(); }, [client]);
  return <QueryClientProvider client={client}>{children}</QueryClientProvider>;
}

export function LaunchSplash() {
  return <main className="shell-splash" aria-label="Hylo" role="status"><img src="/hylo-mark.svg" width="84" height="84" alt="Hylo" /></main>;
}
